import { STATES } from '@reggora_shared_ui/constants/locations'

/**
 * Custom validator for state AutoComplete fields to ensure only valid state codes or names are accepted
 * @param {string} message - Custom error message to display if validation fails
 * @returns {Object} Ant Design form validator object
 */
export const createStateValidator = (message = 'Please select a valid state from the list') => ({
  validator(_, value) {
    if (!value) {
      return Promise.resolve() // Let required validation handle empty values
    }

    // Check if the value is a valid state code OR a valid state name
    if (isValidStateCode(value) || isValidStateName(value)) {
      return Promise.resolve()
    }

    return Promise.reject(new Error(message))
  },
})

/**
 * Check if a value is a valid state code
 * @param {string} value - The value to check
 * @returns {boolean} True if valid state code, false otherwise
 */
export const isValidStateCode = (value) => {
  return Object.keys(STATES).includes(value)
}

/**
 * Check if a value is a valid state name
 * @param {string} value - The value to check
 * @returns {boolean} True if valid state name, false otherwise
 */
export const isValidStateName = (value) => {
  return Object.values(STATES).includes(value)
}

/**
 * Convert state name to state code
 * @param {string} stateName - The state name (e.g., "Alabama")
 * @returns {string} The state code (e.g., "AL") or empty string if not found
 */
export const getStateCodeFromName = (stateName) => {
  const entry = Object.entries(STATES).find(([code, name]) => name === stateName)
  return entry ? entry[0] : ''
}

/**
 * Convert state code to state name
 * @param {string} stateCode - The state code (e.g., "AL")
 * @returns {string} The state name (e.g., "Alabama") or empty string if not found
 */
export const getStateNameFromCode = (stateCode) => {
  return STATES[stateCode] || ''
}

/**
 * Get AutoComplete options for states with state names as values
 * This allows displaying state names in the input while storing state codes in the form
 * @returns {Array} Array of options with state names as values and labels
 */
export const getStateNameOptions = () => {
  return Object.entries(STATES).map(([stateCode, stateName]) => ({
    value: stateName,
    label: stateName,
    code: stateCode,
  }))
}
